import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import DurationDisplay, { formatFileSize } from '../common/DurationDisplay';
import AudioPlayer from '../common/AudioPlayer';
import { format, parseISO } from 'date-fns';

const CATEGORY_OPTIONS = [
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Programa' },
  { value: 'advertising', label: 'Publicidad' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'promo', label: 'Promo' },
  { value: 'playlist', label: 'Playlist' },
  { value: 'special', label: 'Especial' },
  { value: 'fallback', label: 'Fallback' },
];

const CATEGORY_LABELS = CATEGORY_OPTIONS.reduce((acc, c) => {
  acc[c.value] = c.label;
  return acc;
}, {});

export default function MediaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const fetchMedia = async () => {
    try {
      const { data } = await api.get(`/api/media/${id}`);
      setMedia(data);
      setEdit({
        title: data.metadata?.title || '',
        artist: data.metadata?.artist || '',
        album: data.metadata?.album || '',
        original_filename: data.original_filename || '',
        category: data.category || 'music',
      });
    } catch (err) {
      console.error('[MediaDetail] Failed to fetch media:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchMedia();
    }
  }, [id]);

  const handleSave = async () => {
    if (!media) return;
    setSaving(true);
    setSavedMsg('');
    try {
      await api.put(`/api/media/${id}`, {
        title: edit.title,
        artist: edit.artist,
        album: edit.album,
        original_filename: edit.original_filename,
        category: edit.category,
      });
      setSavedMsg('Guardado correctamente');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      console.error('[MediaDetail] Save failed:', err);
      setSavedMsg('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/media/${id}`);
      navigate('/media');
    } catch (err) {
      console.error('[MediaDetail] Delete failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando medio...</p>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="py-12 text-center text-dark-400">
        Medio no encontrado.{' '}
        <Link to="/media" className="text-primary-400">
          Volver
        </Link>
      </div>
    );
  }

  const title = media.metadata?.title || media.original_filename;
  const artist = media.metadata?.artist || '';
  const fileUrl = media.stored_filename ? `/uploads/${media.stored_filename}` : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">{title}</h1>
        <Link to="/media" className="btn btn-secondary btn-sm">
          Volver a la Biblioteca
        </Link>
      </div>

      {fileUrl && media.processing_status === 'ready' && (
        <AudioPlayer src={fileUrl} title={title} artist={artist} />
      )}
      {media.processing_status !== 'ready' && (
        <div className="rounded-lg bg-warning-900/20 border border-warning-700 p-3 text-sm text-warning-400">
          Este archivo aún no está listo para reproducirse (estado: {media.processing_status}).
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-4">
          <div className="flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-lg bg-dark-800 mx-auto">
            <span className="text-3xl">🎧</span>
          </div>

          <div className="rounded-xl bg-dark-850 border border-dark-700 p-4">
            <h3 className="text-sm font-medium text-dark-300 mb-3">Información del archivo</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-dark-400">Categoría</span>
                <select
                  value={edit.category}
                  onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                  className="input input-sm"
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Tamaño</span>
                <span className="text-dark-100">{formatFileSize(media.original_size)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Duración</span>
                <span className="text-dark-100">
                  {media.duration_seconds ? <DurationDisplay seconds={media.duration_seconds} /> : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Formato original</span>
                <span className="text-dark-100">{media.original_format || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Formato procesado</span>
                <span className="text-dark-100">{media.processed_format || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Tamaño procesado</span>
                <span className="text-dark-100">{media.processed_size ? formatFileSize(media.processed_size) : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Bitrate</span>
                <span className="text-dark-100">{media.processed_bitrate ? `${media.processed_bitrate} kbps` : '—'}</span>
              </div>
              {media.created_at && (
                <div className="flex justify-between">
                  <span className="text-dark-400">Subido</span>
                  <span className="text-dark-100">{format(parseISO(media.created_at), 'PPp')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-dark-400">Reproducido</span>
                <span className="text-dark-100">{media.play_count > 0 ? `Sí (${media.play_count}×)` : 'No'}</span>
              </div>
              {media.last_played_at && (
                <div className="flex justify-between">
                  <span className="text-dark-400">Última reproducción</span>
                  <span className="text-dark-100">{format(parseISO(media.last_played_at), 'PPp')}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-dark-100">Metadata</h3>
              {savedMsg && (
                <span className={`text-sm ${savedMsg.startsWith('Guardado') ? 'text-success-400' : 'text-error-400'}`}>
                  {savedMsg}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Nombre / Título</label>
                <input type="text" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Artista</label>
                <input type="text" value={edit.artist} onChange={(e) => setEdit({ ...edit, artist: e.target.value })} className="input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Álbum</label>
                <input type="text" value={edit.album} onChange={(e) => setEdit({ ...edit, album: e.target.value })} className="input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Nombre de archivo</label>
                <input type="text" value={edit.original_filename} onChange={(e) => setEdit({ ...edit, original_filename: e.target.value })} className="input" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-dark-700 pt-6">
        <button type="button" onClick={handleDelete} className="btn btn-error btn-sm">
          Eliminar Medio
        </button>
        <Link to="/media" className="btn btn-secondary">
          Volver
        </Link>
      </div>
    </div>
  );
}
